import React, { useState } from 'react';
import Image from 'next/image';
import styles from '../../style.module.scss';
import { getProductColorValue } from '@/app/utils';
import { IProduct } from '@mybestbrands/types';
import { trackSSTEvent } from '@/app/utils/tracking';

interface DesktopProps {
  images: string[];
  item: IProduct;
  isRelatedProduct?: boolean;
  trackingData?: object | null;
}

const MultipleImagesDesktop = ({ images, item, isRelatedProduct }: DesktopProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [triggeredProducts, setTriggeredProducts] = useState<Set<string>>(new Set()); // State to keep track of triggered products

  const handleMouseLeave = () => {
    setCurrentImageIndex(0);
  };

  const triggerTrackingEvent = () => {
    // Check if this product has already been tracked
    if (!triggeredProducts.has(item.productId)) {
      // Trigger the event only once per product
      trackSSTEvent('product_card_multiple_images_click', { productId: item.productId });

      // Add the productId to the set of triggered products
      setTriggeredProducts((prevSet) => new Set(prevSet).add(item.productId));
    }
  };

  const handleNextImage = (event: React.MouseEvent) => {
    event.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    triggerTrackingEvent(); // Trigger the event for this product
  };

  const handlePrevImage = (event: React.MouseEvent) => {
    event.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    triggerTrackingEvent(); // Trigger the event for this product
  };

  return (
    <div className={styles['imageContainer']} onMouseLeave={handleMouseLeave}>
      <div
        className={styles['leftArrow']}
        style={{
          background: !isRelatedProduct ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
          display: images.length < 2 ? 'none' : '',
        }}
        onClick={handlePrevImage}>
        <Image height={12} width={7} src={'/intstatic/icons/black-arrow-left.svg'} alt={'arrow left'} />
      </div>

      <Image
        src={images[currentImageIndex]}
        unoptimized={true}
        alt={`${item.brandName} ${item.name} ${getProductColorValue(item.colors)}`}
        width={200}
        height={250}
        loading="lazy"
        className={`${styles['hoveredImage']} ${item.clickOutAllowed ? '' : styles['imgUnavailable']}`}
        onError={(e) => {
          (e.target as HTMLImageElement).src = '/intstatic/images/20x25_white.jpg';
        }}
      />
      <div
        className={styles['rightArrow']}
        style={{
          background: !isRelatedProduct ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
          display: images.length < 2 ? 'none' : '',
        }}
        onClick={handleNextImage}>
        <Image height={12} width={7} src={'/intstatic/icons/black-arrow-right.svg'} alt={'arrow right'} />
      </div>
      {images.length > 1 && (
        <div className={styles['dotContainer']}>
          {images.map((_, index) => (
            <span
              key={index}
              className={`${styles.dot} ${index === currentImageIndex ? styles['activeDot'] : ''}`}></span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultipleImagesDesktop;
